import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { HttpHeaders, HttpParams } from "@angular/common/http";

import { AuthService } from "./auth.service";
import { HttpService } from "./http.service";
import { AppConfig } from "../../app.config";
import {
  GatherResponse,
  RawHeadersResponse,
  RawTraceHeadersResponse,
  SegyBucketsResponse,
  SegyDirListing,
  SegyFileInfo,
  SegyGatherParams,
  SegyGeometryResponse,
  SegyHeaderPlotResponse,
  SegyModelGeometryResponse,
  SegySpectrumResponse,
  SegyTraceHeaderResponse,
} from "../../models/segy";

/** Addressing for a single trace-header lookup: an absolute 1-based `trace`,
 * or a gather's addressing + the 0-based `ordinal` clicked within it. */
export interface SegyTraceHeaderQuery {
  trace?: number;
  ordinal?: number;
  scan_field?: string;
  scan_value?: number;
  ffid?: number;
  trace_start?: number;
  trace_count?: number;
  /** custom field tokens ("byte:format") to read alongside the rev1 fields */
  custom?: string[];
}

/**
 * Path-driven client for the standalone single SEG-Y file endpoints
 * (`/api/v1/segy/...`). These address a file directly by `path` + `is_aws`,
 * so a raw input can be inspected without any registered run.
 */
@Injectable({ providedIn: "root" })
export class SegyService {
  constructor(private http: HttpService, private authService: AuthService) {}

  private get baseUrl(): string {
    return `${AppConfig.settings.apiUrl}/segy`;
  }

  getInfo(path: string, isAws: boolean): Observable<SegyFileInfo> {
    return this.http.get(`${this.baseUrl}/info/`, {
      headers: this.getAuthorizationHeader(),
      params: this.pathParams(path, isAws),
    });
  }

  /** List the sub-folders and SEG-Y files of a directory, to find a raw input
   * without typing its full path (no registered run required). */
  listDir(path: string, isAws: boolean): Observable<SegyDirListing> {
    return this.http.get(`${this.baseUrl}/listdir/`, {
      headers: this.getAuthorizationHeader(),
      params: this.pathParams(path, isAws),
    });
  }

  getHeaders(path: string, isAws: boolean): Observable<RawHeadersResponse> {
    return this.http.get(`${this.baseUrl}/headers/`, {
      headers: this.getAuthorizationHeader(),
      params: this.pathParams(path, isAws),
    });
  }

  getTraceHeaders(
    path: string,
    isAws: boolean,
    offset: number,
    limit: number,
    allFields = false
  ): Observable<RawTraceHeadersResponse> {
    let params = this.pathParams(path, isAws)
      .set("offset", offset.toString())
      .set("limit", limit.toString());
    if (allFields) params = params.set("all_fields", "true");
    return this.http.get(`${this.baseUrl}/trace_headers/`, {
      headers: this.getAuthorizationHeader(),
      params,
    });
  }

  getGather(
    path: string,
    isAws: boolean,
    params: SegyGatherParams
  ): Observable<GatherResponse> {
    return this.http.get(`${this.baseUrl}/gather/`, {
      headers: this.getAuthorizationHeader(),
      params: this.gatherParams(path, isAws, params),
    });
  }

  getSpectrum(
    path: string,
    isAws: boolean,
    params: SegyGatherParams
  ): Observable<SegySpectrumResponse> {
    return this.http.get(`${this.baseUrl}/spectrum/`, {
      headers: this.getAuthorizationHeader(),
      params: this.gatherParams(path, isAws, params),
    });
  }

  getGeometry(
    path: string,
    isAws: boolean,
    maxPoints = 20000
  ): Observable<SegyGeometryResponse> {
    const params = this.pathParams(path, isAws).set(
      "max_points",
      maxPoints.toString()
    );
    return this.http.get(`${this.baseUrl}/geometry/`, {
      headers: this.getAuthorizationHeader(),
      params,
    });
  }

  /** Scan the trace headers for cross-plotting. `custom` is a list of
   * "byte:format" tokens to read non-standard header fields alongside the
   * standard ones. */
  getHeaderPlot(
    path: string,
    isAws: boolean,
    maxPoints = 5000,
    custom: string[] = []
  ): Observable<SegyHeaderPlotResponse> {
    let params = this.pathParams(path, isAws).set(
      "max_points",
      maxPoints.toString()
    );
    if (custom.length) params = params.set("custom", custom.join(","));
    return this.http.get(`${this.baseUrl}/header_plot/`, {
      headers: this.getAuthorizationHeader(),
      params,
    });
  }

  /** Survey geometry (origin, bounding box, bin spacing, rotation) of a 3D
   * SEG-Y model / volume, derived from its trace headers. */
  getModelGeometry(
    path: string,
    isAws: boolean,
    sample = 50000
  ): Observable<SegyModelGeometryResponse> {
    const params = this.pathParams(path, isAws).set(
      "sample",
      sample.toString()
    );
    return this.http.get(`${this.baseUrl}/model_geometry/`, {
      headers: this.getAuthorizationHeader(),
      params,
    });
  }

  /** Full header of a single trace — by absolute 1-based `trace`, or by a
   * gather's addressing + the 0-based `ordinal` clicked within it. */
  getTraceHeader(
    path: string,
    isAws: boolean,
    query: SegyTraceHeaderQuery
  ): Observable<SegyTraceHeaderResponse> {
    let params = this.pathParams(path, isAws);
    for (const [key, val] of Object.entries(query)) {
      if (val === undefined || val === null) continue;
      if (key === "custom") {
        const custom = val as string[];
        if (custom.length) params = params.set("custom", custom.join(","));
        continue;
      }
      params = params.set(key, val.toString());
    }
    return this.http.get(`${this.baseUrl}/trace_header/`, {
      headers: this.getAuthorizationHeader(),
      params,
    });
  }

  /** The S3 buckets available for browsing (+ accepted custom-field formats). */
  listBuckets(): Observable<SegyBucketsResponse> {
    return this.http.get(`${this.baseUrl}/buckets/`, {
      headers: this.getAuthorizationHeader(),
    });
  }

  /** path/is_aws + every defined gather/filter param. */
  private gatherParams(
    path: string,
    isAws: boolean,
    params: SegyGatherParams
  ): HttpParams {
    let httpParams = this.pathParams(path, isAws);
    for (const [key, val] of Object.entries(params)) {
      if (val === undefined || val === null) continue;
      httpParams = httpParams.set(key, val.toString());
    }
    return httpParams;
  }

  private pathParams(path: string, isAws: boolean): HttpParams {
    return new HttpParams()
      .set("path", path)
      .set("is_aws", isAws ? "true" : "false");
  }

  private getAuthorizationHeader(): HttpHeaders {
    let headers = new HttpHeaders();
    headers = headers.append(
      "Authorization",
      this.authService.getTokenHeaderText()
    );
    return headers;
  }
}
